import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { routeConstant } from "shared/routes/routeConstant";
import { setAuthReducer } from "shared/redux/reducers/authSlice";
import { GetClientOnboardingProjectService } from "shared/services/project";
import { handleErrors } from "shared/utils/helper";
import {
  canEnterOnboarding,
  onboardingProjectsToEnter,
} from "shared/utils/pendingOnboarding";
import { Icons } from "assets";
import classNames from "classnames";
import DashboardHeader from "../dashboardHeader";
import DashboardSidebar from "../dashboardSidebar";
import MobileNavigation from "../mobileNavigation";
import MobileHeaderNestedPages from "../mobileHeaderNestedPages";
import CustomNotificationButton from "../../common/customNotificationButton";
import TopDrawerNotification from "../../common/topDrawerNotification";
import styles from "./style.module.scss";
import useNotificationCount from "shared/hooks/useNotificationCount";

function getNotificationDrawerMessage(data: any): string {
  if (!data) return "New notification";
  const type = (data.type || "").toLowerCase();
  if (type === "comment_added" || type === "new_comment") return "New comment";
  if (type === "reply_added" || type === "new_reply") return "New reply";
  if (data.title && typeof data.title === "string") return data.title;
  return "New notification";
}

type UserType = "designer" | "client" | "builder" | "collaborator";

interface DashboardWrapperProps {
  children: any;
  sidebarArr: SideBarItemsType[];
  mobileNavItems: MobileNavItem[];
  mainPages: string[];
  userType: UserType;
  showMobileNavConditionally?: boolean;
}

function DashboardWrapper({
  children,
  sidebarArr,
  mobileNavItems,
  mainPages,
  userType,
  showMobileNavConditionally = true,
}: DashboardWrapperProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isScrolled, setIsScrolled] = useState(false);
  const [drawerNotification, setDrawerNotification] = useState<{
    message: string;
  } | null>(null);
  const mainContainerRef = useRef<HTMLElement>(null);

  const nestedPagePattern = new RegExp(`/${userType}/projects/[^/]+`);
  const isUploadRevisionPage =
    location.pathname === routeConstant.designer.home.uploadRevision.path;
  const isNestedPage =
    nestedPagePattern.test(location.pathname) || isUploadRevisionPage;
  const { count: notificationCount } = useNotificationCount();

  const showMobileNav = showMobileNavConditionally
    ? mainPages.includes(location.pathname)
    : true;

  useEffect(() => {
    const mainContainer = mainContainerRef.current;
    if (!mainContainer) return;

    const handleScroll = () => {
      const scrollTop = mainContainer.scrollTop;
      setIsScrolled(scrollTop > 0);
    };

    mainContainer.addEventListener("scroll", handleScroll);
    return () => {
      mainContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleNewNotification = (event: Event) => {
      const customEvent = event as CustomEvent<any>;
      const data = customEvent?.detail;
      setDrawerNotification({
        message: getNotificationDrawerMessage(data),
      });
    };

    window.addEventListener("notification:new", handleNewNotification);
    return () => {
      window.removeEventListener("notification:new", handleNewNotification);
    };
  }, []);

  useEffect(() => {
    const checkClientOnboarding = async () => {
      if (
        !location.pathname.includes("/client") &&
        !location.pathname.includes("/collaborator")
      )
        return;
      // Skip the request entirely when onboarding cannot be entered anyway.
      if (!canEnterOnboarding(location.pathname)) return;

      try {
        const response = await GetClientOnboardingProjectService();
        const raw = response.data?.data ?? response.data;

        const activeOnboarding = onboardingProjectsToEnter({
          pathname: location.pathname,
          list: raw,
        });

        if (activeOnboarding.length > 0) {
          dispatch(
            setAuthReducer({
              onboarding_projects: activeOnboarding,
              onboarding_project: activeOnboarding[0],
            }),
          );
          // Enter the onboarding flow for the first pending project; the
          // flow advances through any remaining ones after each submit.
          navigate(routeConstant.onboarding.path, {
            replace: true,
            state: { projectId: activeOnboarding[0].project_id },
          });
        }
      } catch (error) {
        handleErrors(error);
      }
    };

    checkClientOnboarding();
  }, [location.pathname, dispatch, navigate]);

  return (
    <div className={classNames(styles.topLevelContainer, "p-0 m-0 flex")}>
      <TopDrawerNotification
        message={drawerNotification?.message ?? ""}
        visible={!!drawerNotification}
        onClose={() => setDrawerNotification(null)}
      />
      <aside
        className={classNames(styles.sideNavContainer, styles.sideNavFull)}
        id="sideNav"
      >
        <DashboardSidebar sidebarArr={sidebarArr} />
      </aside>
      <main
        id="mainContainer"
        ref={mainContainerRef}
        className={classNames(styles.mainContainer)}
      >
        <div className={classNames(styles.headerContainer, "lg:block hidden")}>
          <DashboardHeader />
        </div>
        {isNestedPage ? (
          <div
            className={classNames(
              styles.mobileHeaderContainer,
              "lg:hidden block",
              isScrolled ? "bg-[#fcfeff]" : "bg-transparent",
            )}
          >
            <MobileHeaderNestedPages />
          </div>
        ) : (
          <div
            className={classNames(
              isScrolled ? "opacity-0" : "absolute top-4 right-4",
              styles.mobileNotificationHeader,
            )}
          >
            <CustomNotificationButton
              Icon={<Icons.Bell />}
              count={notificationCount}
              to={
                location.pathname.includes("/client")
                  ? routeConstant.client.notifications.path
                  : location.pathname.includes("/collaborator")
                    ? routeConstant.collaborator.notifications.path
                    : location.pathname.includes("/builder")
                      ? routeConstant.builder.notifications.path
                      : routeConstant.designer.notifications.path
              }
            />
          </div>
        )}

        <div id="container-padding" className={classNames(styles.child)}>
          {children}
        </div>
      </main>
      {showMobileNav && !isNestedPage && (
        <div className={classNames(styles.mobileContainer, "")}>
          <MobileNavigation navItems={mobileNavItems} />
        </div>
      )}
    </div>
  );
}

export default DashboardWrapper;
